import React, { useEffect, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { DateTime } from "luxon";
import { sortBy } from "lodash";
import { useHistory, useLocation } from "react-router-dom";
import { RootState } from "typedefs";
import { useTranslation } from "react-i18next";

import ModalLayout from "app/components/Layouts/ModalLayout/ModalLayout";
import SelectLessonModal from "./SelectLessonModal";
import Panel from "./Panel";
import DateAndTime from "./DateAndTime";
import ConfirmBar from "./ConfirmBar";
import { createPurchaseAndGetPath } from "../helpers/createPurchase";
import { isCustomPricing } from "../helpers";
import BookingCards from "./BookingCards";
import usePackageCredits from "../hooks/usePackageCredits";
import {
  setBookSession,
  setIsComplete,
} from "app/actions/scheduling/bookSession";
import { schedulingFetchGet } from "app/utils/request/scheduling/fetch";
import useFacilityLessonRates from "../hooks/useFacilityLessonRates";
import InfoModal from "app/routes/Calendar/InfoModal";

import styles from "./styles.module.scss";
import AuthenticateModal from "app/routes/PublicCalendar/AuthenticateModal";

const SessionForm = ({
  bookLessonData,
  editMode = false,
  disableFacilitySelection = false,
  disableCoachSelection = false,
  isPublicPage = false,
  isForMember = false,
}) => {
  const history = useHistory();
  const dispatch = useDispatch();
  // const location = useLocation();
  const bookLessonState = useSelector((state: RootState) => state.bookSession);

  const user = useSelector((state: RootState) => state.user, shallowEqual);
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const sessionIdFromUrl = params.get("sessionId");


  // const isPrivateRoute = useMemo(() => {
  //   return !location.pathname?.includes?.("public");
  // }, [location]);

  let pricing = null;

  if (sessionIdFromUrl) {
    const values = bookLessonData?.pricingValues[bookLessonState?.coach?.id] || [];
    const matched = values.find(pv => String(pv.id) === String(sessionIdFromUrl));
    if (matched) {
      pricing = matched;
    }
  }

  if (!pricing) {
    pricing = bookLessonState.pricingValue;
  }

  // The store persists. The entry of a custom lesson belongs to the Edit
  // Session page only, so a new booking never starts from it.
  if (isCustomPricing(pricing) && !editMode) {
    pricing = null;
  }

  // Validate that pricing belongs to the current coach or is a facility-level rate.
  // bookSession is persisted via redux-persist, so a stale pricingValue from a
  // previous booking with a different coach can leak into the current session.
  if (
    pricing &&
    bookLessonState?.coach?.id &&
    bookLessonState.coach.id !== "all-coaches" &&
    pricing.coachProfileId &&
    pricing.coachProfileId !== bookLessonState.coach.id &&
    pricing.ownerType !== "Facility"
  ) {
    pricing = null;
  }

  if (!pricing) {
    pricing =
      bookLessonData?.pricingValues[bookLessonState?.coach?.id]?.find(
        (pv) => pv.defaultRate
      );
  }

  const facilityId = bookLessonData.facilities?.[0]?.id;
  const defaultCoach = bookLessonData?.coaches[facilityId]?.[0];

  // Check if facility-level booking options are enabled
  const facility = bookLessonState.facility || bookLessonData.facilities?.[0];
  const facilityBookingEnabled = facility?.featureFlags?.facilityLevelBookingOptions === true;

  // Fetch facility-level rates when enabled
  const { rates: facilityRates } = useFacilityLessonRates(
    facility?.id,
    facilityBookingEnabled && !isPublicPage
  );

  const storeSelection = {
    facility: bookLessonState.facility,
    coach: isPublicPage ? defaultCoach : bookLessonState.coach,
    duration: pricing,
    pricingValue: pricing,
    date: bookLessonState.date ? DateTime.fromISO(bookLessonState.date) : null,
  };

  const [lessonProperties, setLessonProperties] = useState(storeSelection);

  // The option card says whether a package covers the chosen option, which needs
  // the athlete's credits. Scoped to the family member the booking is for, as
  // elsewhere (CFTB-3318).
  const { credits: packageCredits } = usePackageCredits(
    Boolean(user?.self?.id) &&
      (user?.self?.kind === "athlete" || user?.self?.kind === "other"),
    bookLessonState.participantAthleteId,
  );

  // The frame puts the coach's timezone on their card. Resolved against the
  // timezone list, as the other booking screens label it.
  const schedulingTimeZones = useSelector(
    (state: RootState) => state.schedulingTimeZones?.data || [],
  );
  const coachTimezone =
    schedulingTimeZones.find(
      (zone: any) =>
        zone.value ===
        (bookLessonState.coach?.timeZone || bookLessonState.facility?.timeZone),
    )?.display || "";
  const [selectLesson, setSelectLesson] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isPackageErrorModalOpen, setIsPackageErrorModalOpen] = useState<boolean>(false);
  const [purchaseError, setPurchaseError] = useState<{ message?: string; detail?: string } | null>(null);

  const { t } = useTranslation();

  useEffect(() => {
    setLessonProperties(storeSelection);
  }, [bookLessonState.pricingValue, bookLessonData?.pricingValues]);

  // Update pricing when facility rates load (for facility-level booking)
  useEffect(() => {
    const storedPricing =
      isCustomPricing(bookLessonState.pricingValue) && !editMode ? null : bookLessonState.pricingValue;
    if (facilityBookingEnabled && facilityRates.length > 0 && !storedPricing) {
      const defaultFacilityRate = facilityRates.find((rate) => rate.defaultRate) || facilityRates[0];
      if (defaultFacilityRate) {
        setLessonProperties((state) => ({
          ...state,
          pricingValue: defaultFacilityRate,
          duration: defaultFacilityRate,
        }));
      }
    }
  }, [facilityBookingEnabled, facilityRates, bookLessonState.pricingValue, editMode]);

  const saveLessonProperty = (property, value) => {
    setLessonProperties((state) => {
      return {
        ...state,
        [property]: value,
      };
    });
  };

  const setFacilityHandle = (facility) => {
    saveLessonProperty("facility", facility);
    setCoachHandle({
      fullName: t("All Coaches"),
      value: "all-coaches",
      id: "all-coaches",
    });
  };

  const setCoachHandle = (coach) => {
    const defaultRate =
      bookLessonData.rates[coach.id].find((dur) => dur.defaultRate) ||
      bookLessonData.rates[coach.id][0];

    // Use facility rates when enabled, otherwise use coach rates
    const ratesToUse = facilityBookingEnabled && facilityRates.length > 0
      ? facilityRates
      : bookLessonData.pricingValues[coach.id];

    const pricingValue =
      ratesToUse
      ?.filter((value) => !value.shouldDestroy)
      ?.find((dur) => dur.defaultRate) ||
      ratesToUse?.[0];

    setLessonProperties((state) => {
      return {
        ...state,
        duration: defaultRate,
        pricingValue,
        coach,
      };
    });
    dispatch(setBookSession({ time: null }));
  };

  const setDurationHandle = (duration) => {
    saveLessonProperty("duration", duration);
  };

  const viewDateHandle = (date) => {
    dispatch(setBookSession({ date: date.toISO() }));
    saveLessonProperty("date", date);
  };

  const coachesForSelect = sortBy(
    bookLessonData.coaches[lessonProperties.facility.id],
    ["order_number"],
  ).filter((c) => c.facilityId === lessonProperties.facility.id);

  const selectedCoaches = [lessonProperties.coach];
  const availableRates = bookLessonData.rates[lessonProperties.coach.id];

  // Use facility-level rates when enabled, otherwise use coach-level rates
  const pricingValuesForSelect = facilityBookingEnabled && facilityRates.length > 0
    ? facilityRates
    : bookLessonData?.pricingValues[lessonProperties?.coach?.id];

  const fieldsLists = {
    facilitiesList: bookLessonData.facilities,
    coachesList: coachesForSelect,
    ratesList: availableRates,
    pricingValuesList: pricingValuesForSelect,
  };

  const fieldsEnabled = {
    facilities:
      !disableFacilitySelection && bookLessonData.facilities.length > 1,
    coaches: !disableCoachSelection,
    rates: true,
    pricingValues: pricingValuesForSelect && pricingValuesForSelect.length > 0 && !editMode,
  };

  const fieldsHandle = {
    setFacilityHandle,
    setCoachHandle,
    setDurationHandle,
    setPricingValuesHandle: (pricingValue) => {
      saveLessonProperty("pricingValue", pricingValue);
      setDurationHandle({
        label: pricingValue.duration,
        value: pricingValue.durationInMinutes,
        durationInMinutes: pricingValue.durationInMinutes,
      });
    },
  };

  const onSubmitAllCoaches = (sessionPricing) => {
    setSelectLesson(false);

    dispatch(
      setBookSession({
        facility: lessonProperties.facility,
        pricingValue: sessionPricing,
      }),
    );

    if (isForMember) {
      dispatch(setIsComplete(true));
      if (bookLessonState.selectedSession?.id) {
        history.push("/sessions/edit/confirmation");
      } else {
        createPurchaseAndRedirect();
      }
    } else if (isPublicPage) {
      dispatch(setIsComplete(true));
      history.push("/public/confirmation");
    } else {
      if (bookLessonState.selectedSession?.id) {
        history.push("/sessions/edit/confirmation");
      } else {
        createPurchaseAndRedirect();
      }
    }
  };

  const onSubmit = async () => {
    // For only_package rates, check if the student has matching packages before proceeding
    if (lessonProperties.pricingValue?.collectType === 'only_package') {
      // CFTB-3318: when booking for a managed dependent, check that member's
      // packages (not the paying head's); falls back to self for self-booking.
      const userId = bookLessonState.participantAthleteId || user?.self?.id;
      const coachExternalId = lessonProperties.coach?.externalId || bookLessonState?.coach?.externalId;
      const rateId = lessonProperties.pricingValue?.id;

      if (userId && coachExternalId && rateId) {
        try {
          const subscriptions = await schedulingFetchGet(
            `/students/${userId}/subscriptions_by_lesson_rate`,
            { coach_id: coachExternalId, lesson_rate_id: rateId },
          );
          if (!subscriptions || subscriptions.length === 0) {
            setIsPackageErrorModalOpen(true);
            return;
          }
        } catch {
          setIsPackageErrorModalOpen(true);
          return;
        }
      }
    }

    dispatch(
      setBookSession({
        facility: lessonProperties.facility,
        pricingValue: lessonProperties.pricingValue,
      }),
    );

    if (isForMember) {
      dispatch(setIsComplete(true));
    } else if (isPublicPage) {
      dispatch(setIsComplete(true));

      if (user.data === null) {
        // Scroll to top before opening modal
        window.scrollTo(0, 0);
        document.body.scrollTop = 0;

        setIsModalOpen(true);

        // Additional scroll after a brief delay for Safari
        setTimeout(() => {
          window.scrollTo(0, 0);
        }, 50);
      } else {
        history.push("/public/confirmation");
      }
    } else {
      if (bookLessonState.selectedSession?.id) {
        history.push("/sessions/edit/confirmation");
      } else {
        createPurchaseAndRedirect();
      }
    }
  };

  // Use facility rates for modal when enabled
  const modalRatesToUse = facilityBookingEnabled && facilityRates.length > 0
    ? facilityRates
    : bookLessonData.pricingValues[bookLessonState?.coach?.id];

  const pricingValuesForModal = modalRatesToUse?.filter((pv) => {
    return pv?.durationInMinutes === bookLessonState?.duration;
  });

  const closeModal = () => {
    setIsModalOpen(false);
  };

  // Create purchase and redirect to checkout (for logged-in users, non-edit flows).
  // The call itself lives in helpers/createPurchase so the By Date screen can
  // complete a booking through the same path.
  const createPurchaseAndRedirect = async () => {
    const path = await createPurchaseAndGetPath(setPurchaseError);
    if (path) {
      history.push(path);
    }
  };

  return (
    <div className={styles.sessions_form_container}>
      {/* CFTB-3458: the rule under the facility name, which the public header
          strip above this page carries. */}
      {isPublicPage && <div className={styles.top_divider} />}

      {/* CN-2652: the frame stacks the coach and the booking option above a
          single card holding the calendar and that day's slots, rather than a
          left rail of labelled selects. The choices and their handlers are
          unchanged — only where they are presented. */}
      <BookingCards
        timezone={coachTimezone}
        // The cards say what each option would draw on, not just the chosen one.
        packageCredits={packageCredits}
        coaches={fieldsLists.coachesList || []}
        coach={lessonProperties.coach}
        onCoachChange={fieldsHandle.setCoachHandle}
        options={fieldsLists.pricingValuesList || []}
        option={lessonProperties.pricingValue}
        onOptionChange={fieldsHandle.setPricingValuesHandle}
        coachesEnabled={fieldsEnabled.coaches}
        optionsEnabled={fieldsEnabled.pricingValues}
      />

      <div className={styles.book_form_container}>
        <Panel
          lessonProperties={lessonProperties}
          setViewDate={viewDateHandle}
          coaches={selectedCoaches}
        />
        <DateAndTime
          key={selectedCoaches
            .filter((c) => !c.skipSlots)
            .map((c) => c.id)
            .join("-")}
          viewDate={lessonProperties.date}
          onViewDateChange={viewDateHandle}
          coaches={selectedCoaches}
          duration={lessonProperties.duration}
          isPublicPage={isPublicPage}
          hasPricings={
            fieldsLists.pricingValuesList &&
            fieldsLists.pricingValuesList.length > 0
          }
          pricingValue={lessonProperties.pricingValue}
          submitForm={onSubmit}
        />

        <ModalLayout
          isOpen={selectLesson}
          classNames={styles.select_lesson_modal}
        >
          <div className={styles.modal_header}>
            <h2>{t("Select Session")}</h2>
            <a onClick={() => setSelectLesson(false)}>
              <i className={`${styles.modal_close} ico ico-x`} />
            </a>
          </div>

          <SelectLessonModal
            onClose={() => setSelectLesson(false)}
            pricingValues={pricingValuesForModal}
            lessonProperties={lessonProperties}
            onSubmit={onSubmitAllCoaches}
          />
        </ModalLayout>
      </div>
      {/* CN-2652: picking a time selects it, and this bar books it — the frame
          puts the day, the option and the span of the session beside a Confirm.
          It replaces the save bar, whose submit was already hidden (a slot tap
          used to book straight away) and whose cancel the header's back arrow
          covers. */}
      <ConfirmBar
        time={bookLessonState.time}
        optionTitle={lessonProperties.pricingValue?.title}
        onConfirm={onSubmit}
        // CFTB-3426: the bar is sticky at the foot of the screen and covers the
        // last rows of slots, so dropping the selection is the only way to reach
        // a slot underneath it.
        onDismiss={() => dispatch(setBookSession({ time: null }))}
        disabled={
          !bookLessonState.coach ||
          !bookLessonState.coach.lessonRates ||
          bookLessonState.coach.lessonRates.length === 0
        }
      />
      <AuthenticateModal
        isOpen={isModalOpen}
        onClose={closeModal}
        flow='booking'
        urlValues={{ coachName: lessonProperties.coach.fullName, coachId: lessonProperties.coach.externalId }}
        headerValues={{ date: lessonProperties.date }}
      />
      <InfoModal
        isOpen={isPackageErrorModalOpen}
        title={t("Package Required")}
        status="error"
        onDismiss={() => setIsPackageErrorModalOpen(false)}
      >
        <div>
          {t("This session requires a package. Please purchase an offering that includes this booking option.")}
        </div>
      </InfoModal>
      <InfoModal
        isOpen={purchaseError !== null}
        title={purchaseError?.message || t("Unable to create booking")}
        status="error"
        onDismiss={() => setPurchaseError(null)}
      >
        <div>{purchaseError?.detail || t("Please try again.")}</div>
      </InfoModal>
    </div>
  );
};

export default SessionForm;
